import { ApiCollection } from './collection';
import { Observable } from 'rxjs/Observable';
import { ApiServiceInterface } from './api-service.interface';
import { ModelInterface } from './model.interface';
import { DependencyInjector } from '../support/injector';

export class Model implements ModelInterface {

    public id: number;

    public uid: string;

    public discriminator: 'ModelInterface' = 'ModelInterface';

    public _service: ApiServiceInterface;

    protected _original: Object = {};

    protected dates: Array<string> = [];

    protected relations: Object = {};

    protected identifier = 'id';

    protected attributes = {};

    protected casts: { [key: string]: any };

    public static make(data: Object, service): any {
        return new this(data, service);
    }

    public constructor(data?, _service?: ApiServiceInterface) {
        this._service = _service;

        this.boot(data);
    }

    public boot(data: Object = {}) {
        this.fill(data);

        this.setOriginal(data);

        if (!this.uid) {
            this.uid = (Math.floor(Math.random() * 9999999999) + 1000000000).toString();
        }
    }

    public setOriginal(attributes: { [key: string]: any }) {
        this._original = attributes;
    }

    /**
     * Assign attribute values to the model's properties.
     */
    public bootAttributes() {
        this.fill(this.attributes);
    }

    public exists() {
        return !!this.id;
    }

    public fill(data: Object) {
        for (const attribute in data) {
            if (data.hasOwnProperty(attribute)) {
                if (!this.isRelation(attribute) && !(this[attribute] instanceof Function)) {
                    this.setAttribute(attribute, data[attribute]);
                }
            }
        }

        return this;
    }

    public isRelation(key: string) {
        if (this[key] instanceof Function) {
            return true;
        }

        return false;
    }

    public setRelation(key: string, value) {
        this.relations[key] = value;
    }

    public getRelation(key: string) {
        return this.relations[key] ? this.relations[key] : null;
    }

    public setAttribute(key: string, value) {
        this[key] = value;

        if (key !== 'attributes') {
            this.attributes[key] = value;
        }
    }

    public getAttribute(key: string) {
        return this[key] ? this[key] : null;
    }

    public toArray() {
        // return this.attributes;
    }

    public toString() {
        // return JSON.stringify(this.attributes);
    }

    /**
     * Save the instance and determine whether to post or put
     *
     */
    public save(dryRun = false) {
        if (this.id) {
            return this.put(dryRun);
        } else {
            return this.post(dryRun);
        }
    }

    /**
     * Put this instance to the server
     *
     */
    public put(dryRun = false) {
        return new Observable(observer => {
            // const includes = Object.keys(this.relations) ? {'include': Object.keys(this.relations).join(',')} : {};
            this._service.put(this.getIdentifier(), this.plain(), (dryRun ? {'_dry': 1} : {}))
                .subscribe(updatedObject => {
                    this.boot(updatedObject);

                    // save succeeded -> persist current state
                    this.saveState();

                    observer.next(this);
                }, (error) => {
                    observer.error(error);
                }, () => {
                    observer.complete()
                });
        });
    }

    /**
     * Post new instance to the server
     *
     */
    public post(dryRun = false) {
        return new Observable((observer) => {
            const params = dryRun
                ? {'_dry': 1}
                : {};

            this._service
                .post(
                    this.plain(),
                    params
                )
                .subscribe((newObject) => {
                    this.boot(newObject);

                    // save succeeded -> persist current state
                    this.saveState();

                    observer.next(this);
                }, error => {
                    observer.error(error);
                }, () => {
                    observer.complete()
                });
        });
    }

    /**
     * Delete
     */
    public delete() {
        return new Observable(observer => {
            // const includes = Object.keys(this.relations) ? {'include': Object.keys(this.relations).join(',')} : {};
            this._service.delete(this.id).subscribe(newObject => {
                    this.boot(newObject);
                    observer.next(this);
                }, error => {
                    observer.error(error);
                }, () => observer.complete()
            );

        });
    }

    /**
     * Custom request.
     *
     * @param method
     * @param url
     * @param params
     * @returns {any}
     */
    public request(method: string, url: string, params?: Object) {
        const observable = this._service.request(method, url, false, this.plain(), params = {});

        observable.subscribe((object) => {
            this.boot(object);
        });

        return observable;
    }

    /**
     * Get a plain object from the model ready to be pushed to the server
     *
     * @returns {any}
     * @author Gijs Mooren <gijs@kaiserinternet.nl>
     */
    public plain(): any {
        const copy = <any>$.extend(true, {}, this);

        for (const key in copy) {
            if (copy.hasOwnProperty(key)) {
                if (copy[key] !== null && key !== 'plain') {
                    if (key.substr(0, 1) === '_' || key === 'dates' || key === 'attributes' || key === 'relations') {
                        delete copy[key];
                    } else if (typeof copy[key] === 'function') {
                        const functionString = copy[key] + '';

                        if (functionString.indexOf('return this.hasMany') !== -1) {
                            copy[key] = this[key]().all().map(instance => instance.plain());
                        } else if (functionString.indexOf('return this.hasOne') !== -1) {
                            // copy[key] = this[key]().plain();
                        } else {
                            delete copy[key];
                        }
                    } else if (copy[key] instanceof Array) {
                        copy[key] = copy[key].map((model) => {
                            return model.plain instanceof Function ? model.plain() : model;
                        });
                    } else if (<Object>copy[key] instanceof ApiCollection) {
                        // handle collections
                        copy[key] = (<ApiCollection>copy[key]).all().map((model) => {
                            return model.plain instanceof Function ? model.plain() : model;
                        });
                    } else if (<Object>copy[key] instanceof Model) {
                        // handle normal classes that have toJSON methods
                        copy[key] = (<Model>copy[key]).toJson();
                    } else if (copy[key]['toJSON'] && copy[key]['toJSON'] instanceof Function) {
                        // handle normal classes that have toJSON methods
                        copy[key] = (<any>copy[key]).toJSON();
                    }

                    // Format moment to string
                    if (this.dates && this.dates.indexOf(key) >= 0) {
                        copy[key] = (<any>copy[key]).format('YYYY-MM-DD HH:mm:ss');
                    }
                }
            }
        }

        return copy;
    }

    /**
     * Save the current state of all data
     *
     */
    public saveState() {
        this._original = this.plain();
    }

    /**
     * Reset from mutated to original state
     *
     */
    public resetState() {
        this.fill(this._original);
    }

    public toJson() {
        return JSON.parse(JSON.stringify(this.plain()));
    }

    // public static mix(restangularModel, injector: Injector) {
    //
    //     // set the injector
    //     const _self     = new this();
    //     _self._injector = injector;
    //
    //     // make a copy of the data tot avoid async changes
    //     const originalCopy = jQuery.extend({}, restangularModel);
    //
    //     // merge models
    //     const merged = jQuery.extend(true, restangularModel, _self);
    //
    //     this.extendRestangularModel(merged, originalCopy);
    //
    //     return merged;
    // }
    //
    // /**
    //  * Not so fancy hack for copying getters and setters from model to restangular model
    //  *
    //  * @param merged
    //  * @param dataCopy
    //  */
    // private static extendRestangularModel(original: any, extend: any) {
    //     for (let key in this.prototype) {
    //         const descriptor = Object.getOwnPropertyDescriptor(this.prototype, key);
    //
    //         // only re-assign getter and setter properties
    //         if (descriptor && !descriptor.writable) {
    //             Object.defineProperty(original, key, descriptor);
    //             original[key] = extend[key];
    //         }
    //     }
    // }

    public clone(): this {
        return this.newInstance(this.plain());
    }

    public newInstance(attributes: { [key: string]: any } = {}): this {
        return Object.assign(Object.create(this), this);
    }

    protected relationModelBooted(relation: string): boolean {
        if (this[relation] && this._service && this._service.injector) {
            return true;
        }

        return false;
    }

    protected relationIsCached(relation: string): boolean {
        return !!this.relations[relation];
    }

    protected relationHasApiData(relation: string) {
        if (this._original && this._original[relation] && (this._original[relation]['data'] || this._original[relation])) {
            return true;
        }

        return false;
    }

    /**
     * Has many relationship
     *
     * @param modelOrService
     * @param property
     * @param params
     * @returns {ApiCollection}
     * @deprecated
     */
    protected hasMany(modelOrService, property, params = {}) {
        // Return from cache if available
        if (this.relationIsCached(property)) {
            return this.getRelation(property);
        }

        let model, service;

        if (modelOrService.prototype instanceof Model) {
            model   = modelOrService;
            service = {
                injector: DependencyInjector.injector
            };
        } else if (modelOrService.discriminator === 'ApiService') {
            service               = new modelOrService(this._service.injector);
            model                 = service.model;
            service.defaultParams = params;
        } else {
            model   = null;
            service = modelOrService;
        }

        const relationData = this._original[property] ? this._original[property]['data'] || this._original[property] || [] : [];

        // Check if model is booted
        if (!this.relationModelBooted(property)) {
            return ApiCollection.make(relationData, service, model);
        }

        // Check if relation exists in data
        if (!this.relationHasApiData(property)) {
            console.error('Cannot create one to many relation from unknown key ' + property);
            return ApiCollection.make([], service, model);
        }

        const collection = ApiCollection.make(relationData, service, model);

        this.setRelation(property, collection);

        return collection;
    }

    /**
     * Has one relationship
     *
     * @param model
     * @param property
     * @returns {any}
     */
    protected hasOne(modelOrService, property, params = {}) {
        // Return from cache if available
        if (this.relationIsCached(property)) {
            return this.getRelation(property);
        }

        let model, service;

        if (modelOrService.prototype instanceof Model) {
            model   = modelOrService;
            service = {
                injector: this._service.injector
            };
        } else if (modelOrService.prototype.discriminator === 'ApiService') {
            service               = new modelOrService(this._service.injector);
            model                 = service.model;
            service.defaultParams = params;
        } else {
            model   = null;
            service = modelOrService;
        }

        // Check if model is booted
        if (!this.relationModelBooted(property)) {
            return new model;
        }

        // Check if relation exists in data
        if (!this.relationHasApiData(property)) {
            console.error('Cannot create one to one relation from unknown key ' + property);
            return null;
        }

        const relationData = this._original[property]['data'] || this._original[property];

        model = model.make(relationData, service);

        this.setRelation(property, model);

        return model;
    }

    /**
     * Returns the configured identifier
     *
     * @returns {any}
     */
    public getIdentifier() {
        return this[this.identifier];
    }
}



// WEBPACK FOOTER //
// ./libs/chassis-core/src/app/api/model.ts